
import { useState, useEffect } from 'react';
import Icon from '../../../components/ui/AppIcon';
import Enquiry_Form_Modal from '../../enquiry_form_modal/Enquiry_Form_Modal';

const FeeStructure = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [downPayment, setDownPayment] = useState(50000);
  const [tenure, setTenure] = useState(12);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const totalFees = 189000;
  const loanAmount = totalFees - downPayment;
  const monthlyEMI = Math.round(loanAmount / tenure);

  const feeBreakdown = [
    { component: 'Complete Program Fees', amount: 189000 },
    { component: 'Study Materials', amount: 25000 },
    { component: 'Examination Fees', amount: 15000 },
    { component: 'Platform Access', amount: 10000 },
  ];

    const [showStickyModal, setShowStickyModal] = useState(false);

  return (
    <section id="fees-emi" className="py-20 bg-background">
      <div className="max-w-content mx-auto px-section-margin">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Transparent Fee Structure with Easy and Flexible EMI Options
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Affordable education with flexible payment plans designed for working professionals
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Fee Breakdown */}
          <div className="bg-card border border-border rounded-xl p-8">
            <h3 className="text-2xl font-bold text-text-primary mb-6">Complete Fee Breakdown</h3>
            
            <div className="space-y-4 mb-6">
              {feeBreakdown?.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-border">
                  <span className="text-text-primary font-medium">{item?.component}</span>
                  <span className="text-text-primary font-bold">₹{item?.amount?.toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-text-primary font-semibold">Total Program Fees</span>
                <span className="text-3xl font-bold text-primary">₹{totalFees?.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-sm text-text-secondary">All-inclusive, no hidden charges</p>
            </div>

            {/* Benefits */}
            <div className="mt-6 space-y-3">
              {[
                'No admission fees or processing charges',
                'Study materials included in fees',
                'Lifetime access to recorded lectures',
                'Free career counseling sessions',
              ]?.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                  <span className="text-sm text-text-primary">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* EMI Calculator */}
          <div className="bg-gradient-to-br from-accent/5 to-primary/5 border border-border rounded-xl p-8">
            <h3 className="text-2xl font-bold text-text-primary mb-6">EMI Calculator</h3>

            {/* Down Payment Slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-text-primary font-semibold">Down Payment</label>
                <span className="text-2xl font-bold text-primary">₹{downPayment?.toLocaleString('en-IN')}</span>
              </div>
              <input
                type="range"
                min="25000"
                max="150000"
                step="5000"
                value={downPayment}
                onChange={(e) => isHydrated && setDownPayment(Number(e?.target?.value))}
                className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-text-secondary mt-2">
                <span>₹25,000</span>
                <span>₹1,50,000</span>
              </div>
            </div>

            {/* Tenure Slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-text-primary font-semibold">Loan Tenure</label>
                <span className="text-2xl font-bold text-primary">{tenure} Months</span>
              </div>
              <input
                type="range"
                min="6"
                max="24"
                step="6"
                value={tenure}
                onChange={(e) => isHydrated && setTenure(Number(e?.target?.value))}
                className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-text-secondary mt-2">
                <span>6 Months</span>
                <span>24 Months</span>
              </div>
            </div>

            {/* EMI Result */}
            <div className="bg-background rounded-xl p-6 border-2 border-accent shadow-cta">
              <div className="text-center mb-4">
                <div className="text-sm text-text-secondary mb-2">Your Monthly EMI</div>
                <div className="text-4xl font-bold text-accent">₹{monthlyEMI?.toLocaleString('en-IN')}</div>
                <div className="text-sm text-text-secondary mt-2">for {tenure} months</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div className="text-center">
                  <div className="text-xs text-text-secondary mb-1">Loan Amount</div>
                  <div className="font-bold text-text-primary">₹{loanAmount?.toLocaleString('en-IN')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-text-secondary mb-1">Total Payable</div>
                  <div className="font-bold text-text-primary">₹{totalFees?.toLocaleString('en-IN')}</div>
                </div>
              </div>
            </div>

            <button className="w-full mt-6 bg-accent text-accent-foreground py-4 rounded-lg font-bold shadow-cta hover:bg-accent/90 transition-default" onClick={() => {setShowStickyModal(true)}}>
              Apply for EMI - Get Instant Approval
            </button>

            <p className="text-xs text-text-secondary text-center mt-4">
              *Interest-free EMI available. Processing time: 24-48 hours
            </p>
          </div>
        </div>

        {/* Scholarship Info */}
        <div className="mt-12 bg-warning/10 border border-warning/20 rounded-xl p-8 text-center">
          <Icon name="SparklesIcon" size={48} className="text-warning mx-auto mb-4" variant="solid" />
          <h3 className="text-2xl font-bold text-text-primary schollar_tile mb-3">Early Bird Scholarship – Limited Seats Available</h3>
          <p className=" mb-6 max-w-2xl mx-auto schollar_subtitle">
           Secure up to 20000 scholarship based on academic profile and work experience.
          </p>
          <p className=' mb-6 max-w-2xl mx-auto schollar_simple_text'>⏳ Early applicants get priority benefits. Apply now to check eligibility.</p>
          <button className="bg-warning text-warning-foreground px-8 py-2 rounded-lg font-semibold hover:bg-warning/90 transition-default"  onClick={() => {setShowStickyModal(true)}}>
            Check Scholarship Eligibility
          </button>
        </div>
      </div>

         <Enquiry_Form_Modal
          show={showStickyModal}
          onHide={() => {
            setShowStickyModal(false);
          }}
        />
    </section>
  );
};

export default FeeStructure;